/**
 * Utilities for price-per-seat / zone-based seat pricing (precios por asiento).
 * Used for listado por zona during seat selection and in payment details.
 * Prefers seatFareDescription (tariff/zone from backend); category only when it's a tariff name (VIP, general), not layout type (seat, seat_tv).
 */

import ELEMENTS_ZONE_COLORS from 'constants/elementsZoneColors';

/** Layout types that are not user-friendly for listado; do not use as label. */
const LAYOUT_ONLY_CATEGORIES = ['seat', 'seat_tv', 'hallway', 'hallway_adjacent'];

const DEFAULT_ZONE_LABEL = 'General';
const DEFAULT_ZONE_KEY = '_general';

/** Normalize any zone-like reference into a comparable string key. */
const normalizeZoneReference = (value) => String(value ?? '').trim();
/** Normalize user-facing zone description values. */
const normalizeZoneDescription = (value) => String(value ?? '').trim();

/** Normalize raw seat price zones from API (camelCase/snake_case). */
const normalizeSeatPriceZones = (seatPriceZones) =>
  Array.isArray(seatPriceZones)
    ? seatPriceZones
        .filter(
          (item) => item && (item.seatFareReference != null || item.seat_fare_reference != null),
        )
        .map((item) => ({
          seatFareReference: normalizeZoneReference(
            item.seatFareReference ?? item.seat_fare_reference,
          ),
          seatFareDescription: normalizeZoneDescription(
            item.seatFareDescription ?? item.seat_fare_description,
          ),
          price: Number(item.price ?? 0),
        }))
    : [];

/** Get the best available zone reference from a seat object. */
const getSeatReference = (seat = {}) =>
  normalizeZoneReference(
    seat?.seatFareReference ??
      seat?.seat_fare_reference ??
      seat?.seatFareId ??
      seat?.seat_fare_id ??
      seat?.seatFareDescription ??
      seat?.seat_fare_description ??
      seat?.category,
  );

/**
 * Extract seat price zones from a trip/details-like source object.
 * Supports direct fields, details requests and nested trip/bus/meta objects.
 */
export const getSeatPriceZonesFromSource = (source = {}) => {
  if (!source || typeof source !== 'object') return [];

  const direct = source?.seatPriceZones ?? source?.seat_price_zones;
  // Empty [] is not authoritative — zones often live on fragments or details_requests only.
  if (Array.isArray(direct) && direct.length > 0) return direct;

  if (Array.isArray(source.fragments) && source.fragments.length > 0) {
    const fromFragments = source.fragments
      .map((fragment) => {
        const plain =
          fragment && typeof fragment === 'object' && typeof fragment.toJS === 'function'
            ? fragment.toJS()
            : fragment;
        return getSeatPriceZonesFromSource(plain);
      })
      .find((zones) => Array.isArray(zones) && zones.length > 0);
    if (fromFragments) return fromFragments;
  }

  let firstDetailsRequest = null;
  if (Array.isArray(source?.detailsRequests)) {
    [firstDetailsRequest] = source.detailsRequests;
  } else if (Array.isArray(source?.details_requests)) {
    [firstDetailsRequest] = source.details_requests;
  }

  const nested = firstDetailsRequest?.seatPriceZones ?? firstDetailsRequest?.seat_price_zones ?? [];
  if (Array.isArray(nested) && nested.length > 0) return nested;

  // Some flows keep pricing zones inside nested objects.
  const nestedCandidates = [source?.trip, source?.bus, source?.meta].filter(Boolean);
  const firstCandidateWithZones = nestedCandidates
    .map((candidate) => getSeatPriceZonesFromSource(candidate))
    .find((zones) => Array.isArray(zones) && zones.length > 0);
  if (firstCandidateWithZones) return firstCandidateWithZones;

  return [];
};

/** Immutable to plain when applicable. */
const toPlainMaybe = (v) =>
  v && typeof v === 'object' && typeof v.toJS === 'function' ? v.toJS() : v;

/**
 * Trip ids used as keys in Redux `seatsBus` (SET_SEAT_MAP_TRIPS — same as `firstTrip.trip.id`).
 * @param {Object} way - departs or returns.
 * @returns {string[]}
 */
const extractSeatsBusTripIdsFromWay = (way = {}) => {
  const w = toPlainMaybe(way);
  const ids = [];
  if (w.tripSlug != null) ids.push(String(w.tripSlug));
  if (w.tripId != null) ids.push(String(w.tripId));
  if (w.trip?.id != null) ids.push(String(w.trip.id));
  if (Array.isArray(w.fragments)) {
    w.fragments.forEach((fr) => {
      const f = toPlainMaybe(fr);
      if (f?.id != null) ids.push(String(f.id));
      if (f?.trip?.id != null) ids.push(String(f.trip.id));
    });
  }
  return [...new Set(ids.filter(Boolean))];
};

/**
 * Zones cached when `fetchTripDetails` ran with saveIn !== 'purchase' (only `seatsBus` is updated).
 * @param {Object} seatsBus - `state.seatsBus.toJS()`.
 * @param {string[]} tripIds - From {@link extractSeatsBusTripIdsFromWay}.
 * @returns {Array<{ seatFareReference: string, price: number }>}
 */
const mergeSeatPriceZonesFromSeatsBus = (seatsBus, tripIds) => {
  if (
    !seatsBus ||
    typeof seatsBus !== 'object' ||
    !Array.isArray(tripIds) ||
    tripIds.length === 0
  ) {
    return [];
  }
  const merged = [];
  const seen = new Set();
  tripIds.forEach((tid) => {
    const entry = seatsBus[tid];
    if (!entry) return;
    let zones = entry.seatPriceZones;
    if (zones && typeof zones.toJS === 'function') zones = zones.toJS();
    if (!Array.isArray(zones)) return;
    normalizeSeatPriceZones(zones).forEach((z) => {
      const ref = z.seatFareReference;
      if (ref && !seen.has(ref)) {
        seen.add(ref);
        merged.push(z);
      }
    });
  });
  return merged;
};

/** Merge zone arrays by seat fare reference while keeping first occurrence. */
const mergeSeatPriceZones = (...zoneLists) => {
  const merged = [];
  const seen = new Set();

  zoneLists.forEach((list) => {
    if (!Array.isArray(list)) return;
    normalizeSeatPriceZones(list).forEach((zone) => {
      const reference = zone.seatFareReference;
      if (!reference || seen.has(reference)) return;
      seen.add(reference);
      merged.push(zone);
    });
  });

  return merged;
};

/**
 * Zones for passenger UI: purchase leg shape first, then Redux `seatsBus` (pre–purchase seat step).
 *
 * @param {Object} way - `purchase.departs` or `purchase.returns` (plain).
 * @param {Object} [seatsBus={}] - `state.seatsBus.toJS()` optional.
 * @returns {Array<Object>}
 */
export const getSeatPriceZonesForPassengersLeg = (way = {}, seatsBus = {}) => {
  const fromPurchase = getSeatPriceZonesFromSource(way);
  const tripIds = extractSeatsBusTripIdsFromWay(way);
  const fromSeatsBus = mergeSeatPriceZonesFromSeatsBus(seatsBus, tripIds);
  return mergeSeatPriceZones(fromPurchase, fromSeatsBus);
};

/** Derive distinct seat price zones from a seats array. */
export const deriveSeatPriceZonesFromSeats = (seats = []) => {
  const list = Array.isArray(seats) ? seats : [];
  const seen = new Set();
  const derived = [];

  list.forEach((seat) => {
    const reference = getSeatReference(seat);
    if (!reference || seen.has(reference)) return;
    seen.add(reference);
    derived.push({
      seatFareReference: reference,
      seatFareDescription: normalizeZoneDescription(
        seat?.seatFareDescription ?? seat?.seat_fare_description,
      ),
      price: Number(seat?.price ?? 0),
    });
  });

  return derived;
};

/** Derive distinct seat price zones from a bus layout matrix. */
export const deriveSeatPriceZonesFromLayout = (layout = []) => {
  if (!Array.isArray(layout)) return [];
  const seats = layout.flat(2).filter((item) => item && item.number != null);
  return deriveSeatPriceZonesFromSeats(seats);
};

/**
 * Stable sort so zoneNumber/color do not depend on API array order.
 * Same references always map to the same Elements zone (1–5) everywhere (map, listado, pasajeros).
 */
const sortZonesForStableZoneNumbers = (zones) => {
  return [...zones].sort((a, b) => {
    return String(a.seatFareReference).localeCompare(String(b.seatFareReference), undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  });
};

/** Build an indexed zone map used by seat rendering and summaries. */
export const buildZoneMap = ({ seatPriceZones = [] } = {}) => {
  const normalizedSeatPriceZones = sortZonesForStableZoneNumbers(
    normalizeSeatPriceZones(seatPriceZones),
  );

  return normalizedSeatPriceZones.reduce((acc, zone, index) => {
    const zoneNumber = (index % 5) + 1;
    acc[zone.seatFareReference] = {
      zoneKey: zone.seatFareReference,
      zoneNumber,
      label: zone.seatFareDescription || zone.seatFareReference || DEFAULT_ZONE_LABEL,
      color: ELEMENTS_ZONE_COLORS[zoneNumber] || null,
      price: zone.price,
      order: index,
    };

    return acc;
  }, {});
};

/** Internal alias for consistency when reading seat reference keys. */
const getReferenceFromSeat = (seat = {}) => getSeatReference(seat);

/** Fallback: match a zone using seat fare description when reference is unavailable. */
const getZoneFromDescription = (seat = {}, zoneMap = {}) => {
  const seatFareDescription = normalizeZoneReference(
    seat?.seatFareDescription ?? seat?.seat_fare_description,
  );
  if (!seatFareDescription) return null;

  const normalizedDescription = seatFareDescription.toLowerCase();
  return (
    Object.values(zoneMap).find((zone) => {
      const normalizedZoneKey = normalizeZoneReference(zone?.zoneKey).toLowerCase();
      const normalizedZoneLabel = normalizeZoneReference(zone?.label).toLowerCase();
      return (
        normalizedDescription === normalizedZoneKey || normalizedDescription === normalizedZoneLabel
      );
    }) || null
  );
};

/**
 * Get zone key and label for a seat. Prefers seatFareDescription; uses category only when it's a tariff (VIP, general), not "seat".
 * @param {Object} seat - Seat object with optional seatFareId, seatFareDescription, category
 * @returns {{ zoneKey: string, label: string }}
 */
export const getSeatZone = (seat, zoneMap = {}) => {
  const seatReference = getReferenceFromSeat(seat);
  const zoneFromMap =
    (seatReference ? zoneMap[seatReference] : null) || getZoneFromDescription(seat, zoneMap);
  const zoneKey = zoneFromMap?.zoneKey || seatReference || seat.category || DEFAULT_ZONE_KEY;
  const categoryAsLabel =
    seat.category && !LAYOUT_ONLY_CATEGORIES.includes(seat.category) ? seat.category : null;
  const label =
    zoneFromMap?.label ?? seat.seatFareDescription ?? categoryAsLabel ?? DEFAULT_ZONE_LABEL;

  return {
    zoneKey,
    label,
    zoneNumber: zoneFromMap?.zoneNumber ?? 0,
    color: zoneFromMap?.color ?? null,
  };
};

/**
 * Compare seat numbers regardless of number/string formatting.
 * @param {string|number|undefined|null} a
 * @param {string|number|undefined|null} b
 * @returns {boolean}
 */
export const sameSeatNumber = (a, b) => String(a ?? '').trim() === String(b ?? '').trim();

/**
 * Seat label from a selected-seat row (`number`, `seat`, or `seat_number`).
 * @param {object} [seatObj]
 * @returns {string|number|undefined}
 */
const seatNumberFromSelectionObject = (seatObj) => {
  const n = seatObj?.number ?? seatObj?.seat ?? seatObj?.seat_number;
  if (n != null && String(n).trim() !== '') return n;
  return undefined;
};

/**
 * Seat number for purchase-complete passenger rows when API `ticket.seat` is empty
 * (common for some gratuity payloads). Pairs ticket to `fragment.selectedSeats`
 * positionally by row index, with a single-seat fallback.
 *
 * @param {object} ticket - Fragment ticket row.
 * @param {Array<object>} selectedSeats - `fragment.selectedSeats` from purchase.
 * @param {number} ticketIndex - Index of this ticket in `fragment.tickets`.
 * @returns {string|number|undefined}
 */
export const resolveTicketSeatNumberForDisplay = (
  ticket = {},
  selectedSeats = [],
  ticketIndex = 0,
) => {
  const fromTicket = ticket?.seat;
  if (fromTicket != null && String(fromTicket).trim() !== '') {
    return fromTicket;
  }
  const seats = Array.isArray(selectedSeats) ? selectedSeats : [];
  const fromPicked = seatNumberFromSelectionObject(seats[ticketIndex]);
  if (fromPicked != null) return fromPicked;
  if (seats.length === 1) {
    const only = seatNumberFromSelectionObject(seats[0]);
    if (only != null) return only;
  }
  return undefined;
};

/** Prefer fare reference, then fare description as a seat zone key. */
const getSeatZoneKeyCandidate = (seat = {}) => seat?.seatFareReference ?? seat?.seatFareDescription;

/** Prefer fare description, then fare reference as display label. */
const getSeatZoneLabelCandidate = (seat = {}) =>
  seat?.seatFareDescription ?? seat?.seatFareReference;

/**
 * Resolve seat zone for pricing details. If ticket lacks direct fare reference,
 * fallback to matching by seat price against zone prices.
 * @param {Object} ticketData - Ticket/seat row data.
 * @param {Object} zoneMap - Zone map keyed by reference.
 * @param {number} seatPrice - Seat effective price.
 * @returns {{color: string|null, label: string, zoneKey: string}}
 */
export const resolveSeatZoneForDetails = (ticketData, zoneMap, seatPrice) => {
  const seatFromSelection = (ticketData?.selectedSeatsForTrip || []).find((seat) => {
    return sameSeatNumber(seat?.number, ticketData?.seat);
  });
  if (seatFromSelection) {
    const persistedZoneNumber = Number(seatFromSelection?.seatZoneNumber ?? 0);
    if (persistedZoneNumber > 0) {
      const labelCandidate =
        getSeatZoneLabelCandidate(seatFromSelection) ?? getSeatZoneLabelCandidate(ticketData);
      const zoneKeyCandidate =
        getSeatZoneKeyCandidate(seatFromSelection) ?? getSeatZoneKeyCandidate(ticketData);

      return {
        color: ELEMENTS_ZONE_COLORS[persistedZoneNumber] || null,
        label: labelCandidate ?? 'General',
        zoneKey: zoneKeyCandidate ?? 'General',
      };
    }
    const fromSelectedSeat = getSeatZone(seatFromSelection, zoneMap);
    if (fromSelectedSeat?.color) return fromSelectedSeat;
  }

  const fromReference = getSeatZone(ticketData, zoneMap);
  if (fromReference?.color) return fromReference;

  const zoneByPrice = Object.values(zoneMap || {}).find(
    (zone) => Number(zone?.price) === Number(seatPrice),
  );
  if (!zoneByPrice) return fromReference;

  return {
    color: zoneByPrice.color || null,
    label: zoneByPrice.label || zoneByPrice.zoneKey,
    zoneKey: zoneByPrice.zoneKey,
  };
};

/**
 * Resolves Elements `zone` (1–5) for a passenger seat chip when `seat_price_zones` is available.
 * @param {Object} seat - Seat object (selected seat from purchase state).
 * @param {Array<Object>} seatPriceZones - Zones array for that trip leg (departure or return).
 * @returns {number|undefined} Zone index for NewSeat, or undefined for default styling.
 */
export const getSeatZoneNumberForPassengerSeat = (seat, seatPriceZones) => {
  if (seat?.seatZoneNumber != null && Number(seat.seatZoneNumber) > 0) {
    return Number(seat.seatZoneNumber);
  }
  if (!seat || !Array.isArray(seatPriceZones) || seatPriceZones.length === 0) return undefined;
  const zoneMap = buildZoneMap({ seatPriceZones });
  const { zoneNumber } = getSeatZone(seat, zoneMap);
  return zoneNumber > 0 ? zoneNumber : undefined;
};

/**
 * Comparable numeric price from ticket-like payloads (price, amount, total, subtotal).
 * @param {object} [ticket]
 * @returns {number}
 */
export const getTicketPrice = (ticket = {}) =>
  Number(ticket.price ?? ticket.amount ?? ticket.total ?? ticket.subtotal ?? 0);

/**
 * Resolve NewSeat zone index (1–5) from ticket fare reference, selected seat match, or price vs zones.
 * @param {object} [ticket]
 * @param {Array<object>} [seatPriceZones]
 * @param {Array<object>} [selectedSeats]
 * @returns {number|undefined}
 */
export const resolveTicketSeatZone = (ticket = {}, seatPriceZones = [], selectedSeats = []) => {
  const effectiveSeatPriceZones =
    Array.isArray(seatPriceZones) && seatPriceZones.length > 0
      ? seatPriceZones
      : deriveSeatPriceZonesFromSeats(selectedSeats);

  const matchedSelectedSeat = (selectedSeats || []).find((seat) => {
    return sameSeatNumber(seat?.number, ticket?.seat);
  });
  if (matchedSelectedSeat) {
    const selectedSeatZone = getSeatZoneNumberForPassengerSeat(
      matchedSelectedSeat,
      effectiveSeatPriceZones,
    );
    if (selectedSeatZone) return selectedSeatZone;
  }

  const directZone = getSeatZoneNumberForPassengerSeat(ticket, effectiveSeatPriceZones);
  if (directZone) return directZone;

  const ticketPrice = getTicketPrice(ticket);
  if (
    !ticketPrice ||
    !Array.isArray(effectiveSeatPriceZones) ||
    effectiveSeatPriceZones.length === 0
  ) {
    return undefined;
  }

  const matchedZone = effectiveSeatPriceZones.find(
    (zone) => Number(zone?.price ?? 0) === ticketPrice,
  );
  if (!matchedZone) return undefined;

  const zoneFromPrice = getSeatZoneNumberForPassengerSeat(
    {
      seatFareReference: matchedZone.seatFareReference,
      seat_fare_reference: matchedZone.seat_fare_reference,
    },
    effectiveSeatPriceZones,
  );
  return zoneFromPrice;
};

/**
 * Get effective price for a seat (from backend).
 * @param {Object} seat - Seat object with optional price
 * @returns {number}
 */
export const getSeatPrice = (seat) => Number(seat.price ?? 0);

/**
 * Group selected seats by zone and compute subtotals and total.
 * Excludes adjacent seats (they are shown separately in pricing).
 *
 * @param {Array<Object>} selectedSeats - Selected seat objects (number, price, category, seatFareId, seatFareDescription)
 * @param {Object} options - Options
 * @param {boolean} [options.includeAdjacent=false] - If true, include isPickedAsAdjacent seats in grouping
 * @param {Object} [options.zoneMap={}] - Zone map keyed by seat fare reference.
 * @returns {{ groups: Array<{ zoneKey: string, label: string, count: number, subtotal: number }>, total: number }}
 */
export const groupSeatsByZone = (selectedSeats, { includeAdjacent = false, zoneMap = {} } = {}) => {
  const seatsToGroup = includeAdjacent
    ? selectedSeats
    : (selectedSeats || []).filter((seat) => !seat.isPickedAsAdjacent);

  const byZone = {};

  seatsToGroup.forEach((seat) => {
    const { zoneKey, label, color } = getSeatZone(seat, zoneMap);
    const zoneOrder = zoneMap?.[zoneKey]?.order ?? Number.MAX_SAFE_INTEGER;
    const price = getSeatPrice(seat);

    if (!byZone[zoneKey]) {
      byZone[zoneKey] = { zoneKey, label, color, order: zoneOrder, count: 0, subtotal: 0 };
    }
    byZone[zoneKey].count += 1;
    byZone[zoneKey].subtotal += price;
  });

  const groups = Object.values(byZone).sort((a, b) => {
    if (a.order !== b.order) return a.order - b.order;
    return a.label.localeCompare(b.label);
  });
  const total = groups.reduce((sum, g) => sum + g.subtotal, 0);

  return { groups, total };
};
